import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import type { ButtonProps } from '@atlaskit/button/default/button';
import HomeIcon from '@atlaskit/icon/core/home';
import LogInIcon from '@atlaskit/icon/core/log-in';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { NOT_FOUND_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getLoginLink } from '@confluence/login-if-anonymous/entry-points/getLoginLink.ts';
import { NOT_FOUND_LOAD_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { HOME, SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { withRoutesContext } from '@confluence/route-manager/entry-points/withRoutesContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import { ErrorPageComponent } from './ErrorPageComponent.tsx';
import notFoundImage from './NotFoundIcon.svg';
import notFoundLightImageMohiti from './NotFoundLightMohiti.svg';
import notFoundDarkImageMohiti from './NotFoundDarkMohiti.svg';
import { NOT_FOUND_PAGE_LOAD } from './perf.config.ts';

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'NOT_FOUND',
};

export const NotFound: React.FC<{ secondaryButtonProps?: ButtonProps }> = withRoutesContext(
	({ push, toUrl, secondaryButtonProps }) => {
		const { homepageUri } = useContext(SPAViewContext);
		const primaryButtonUrl = ['/spa.action', '', 'homepageUri'].includes(homepageUri)
			? toUrl(HOME.name)
			: toUrl(SPACE_OVERVIEW.name, { href: `${CONTEXT_PATH}${homepageUri}` });

		const { isLoggedIn } = useSessionData();
		const { createEvent } = useConfluenceAnalyticsEvents();

		useEffect(() => {
			createEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'notFoundScreen',
					action: 'viewed',
					actionSubject: 'page',
					actionSubjectId: 'notFoundPage',
					attributes: {
						isLoggedIn,
						isMohitiExperiment: expValEquals(
							'cc_mohiti_broken_error_restricted_screens_exp',
							'isEnabled',
							true,
						),
					},
				},
			}).fire();
		}, [createEvent, isLoggedIn]);

		const logInButtonProps = {
			children: (
				<FormattedMessage
					id="not-found.link.to.log-in"
					defaultMessage="Log in"
					description="Link button to log in"
				/>
			),
			onClick: () => push(getLoginLink()),
			iconBefore: LogInIcon,
		};
		const secondaryButton =
			!isLoggedIn && !secondaryButtonProps ? logInButtonProps : secondaryButtonProps;

		const { overrideUFORouteName } = useRouteActions();
		useEffect(() => {
			overrideUFORouteName(NOT_FOUND_LOAD_KEY);
		}, [overrideUFORouteName]);

		return (
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<ExperienceStart name={NOT_FOUND_EXPERIENCE} />
				<div data-testid="confluence-not-found">
					<ErrorPageComponent
						errorImage={
							expValEquals('cc_mohiti_broken_error_restricted_screens_exp', 'isEnabled', true)
								? notFoundLightImageMohiti
								: notFoundImage
						}
						errorImageDark={
							expValEquals('cc_mohiti_broken_error_restricted_screens_exp', 'isEnabled', true)
								? notFoundDarkImageMohiti
								: undefined
						}
						layout={
							expValEquals('cc_mohiti_broken_error_restricted_screens_exp', 'isEnabled', true)
								? 'centered'
								: 'default'
						}
						errorTitle={
							expValEquals('cc_mohiti_broken_error_restricted_screens_exp', 'isEnabled', true) ? (
								<FormattedMessage
									id="not-found.header.mohiti"
									defaultMessage="Well, this is awkward"
									description="Header line of a 404 Not Found page in the Mohiti error screen experience"
								/>
							) : (
								<FormattedMessage
									id="not-found.header"
									defaultMessage="We couldn’t find what you’re looking for"
									description="Header line of a 404 Not Found page for any content type"
								/>
							)
						}
						errorMessage={
							expValEquals('cc_mohiti_broken_error_restricted_screens_exp', 'isEnabled', true) ? (
								<FormattedMessage
									id="not-found.description.mohiti"
									defaultMessage="We can’t find that page. It might have been moved, deleted, or the URL has a typo."
									description="Line below the 404 Not Found header line in the Mohiti error screen experience"
								/>
							) : (
								<FormattedMessage
									id="not-found.description"
									defaultMessage="It may have been deleted, the URL could have a typo in it, or you may need to log in to view it."
									description="Line below the 404 Not Found header line"
								/>
							)
						}
						primaryButtonProps={{
							children: (
								<FormattedMessage
									id="not-found.link.to.homepage"
									defaultMessage="Go home"
									description="A button that links to website's homepage"
								/>
							),
							iconBefore: expValEquals(
								'cc_mohiti_broken_error_restricted_screens_exp',
								'isEnabled',
								true,
							)
								? undefined
								: HomeIcon,
							onClick: () => {
								createEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'notFoundGoHomeButton',
										source: 'notFoundScreen',
										attributes: {
											isMohitiExperiment: expValEquals(
												'cc_mohiti_broken_error_restricted_screens_exp',
												'isEnabled',
												true,
											),
										},
									},
								}).fire();
								push(primaryButtonUrl);
							},
						}}
						secondaryButtonProps={secondaryButton}
					/>
					<PageLoadEnd metric={NOT_FOUND_PAGE_LOAD} customData={CUSTOM_ATTRIBUTES} />
					<ExperienceSuccess name={NOT_FOUND_EXPERIENCE} />
				</div>
			</ErrorBoundary>
		);
	},
);

NotFound.displayName = 'NotFound';
